import type { FormatType, PickerMode } from '@rc-component/picker/interface';
import type { DateTime } from 'luxon';

/** A committed or pending range, as rc-picker reports it. */
export type Range = [start: DateTime | null, end: DateTime | null];
/** A range as consumers may hand it in — `undefined` halves included. */
export type LooseRange = [start?: DateTime | null, end?: DateTime | null];

type RangeFormat =
  | FormatType<DateTime>
  | Array<FormatType<DateTime>>
  | { format: string; type?: 'mask' };

type AllowEmpty = boolean | [boolean, boolean];

/** The modes where rc defaults `needConfirm` to true, so it demands one OK per half. */
export const needsSingleConfirm = ({
  picker = 'date',
  showTime,
  needConfirm,
}: {
  picker?: PickerMode;
  showTime?: boolean | object;
  needConfirm?: boolean;
}): boolean => {
  if (needConfirm !== undefined) return false;
  const internalPicker = picker === 'date' && showTime ? 'datetime' : picker;
  return internalPicker === 'datetime' || internalPicker === 'time';
};

export const changedHalf = (prev: Range | null, next: Range): 'start' | 'end' | 'both' | null => {
  const [prevStart, prevEnd] = prev ?? [null, null];
  const startMoved = !isSameTime(prevStart, next[0]);
  const endMoved = !isSameTime(prevEnd, next[1]);

  if (startMoved && endMoved) return 'both';
  if (startMoved) return 'start';
  if (endMoved) return 'end';
  return null;
};

/** Keep the range length when the start moves. Calendar days, so DST does not slip the clock. */
export const applyKeepDuration = (
  prev: Range | null,
  next: Range,
  bounds: { minDate?: DateTime; maxDate?: DateTime } = {},
): Range => {
  const [prevStart, prevEnd] = prev ?? [null, null];
  const [nextStart, nextEnd] = next;

  if (prevStart == null || prevEnd == null) return next;
  if (nextStart == null || nextEnd == null) return next;
  if (+nextEnd !== +prevEnd) return next;
  if (+nextStart === +prevStart) return next;

  const shifted = nextStart.plus(prevEnd.diff(prevStart, ['days', 'milliseconds']));
  if (bounds.minDate != null && shifted < bounds.minDate) return next;
  if (bounds.maxDate != null && shifted > bounds.maxDate) return next;

  return [nextStart, shifted];
};

/** Swap the halves when they are out of order, as rc-picker does on its own commit. */
export const sortRange = (range: Range, ordered: boolean): Range => {
  const [start, end] = range;
  if (!ordered || start == null || end == null) return range;
  return +start > +end ? [end, start] : range;
};

export const isCommittable = (range: Range, allowEmpty: AllowEmpty = false): boolean => {
  const [startMayBeEmpty, endMayBeEmpty] = Array.isArray(allowEmpty)
    ? allowEmpty
    : [allowEmpty, allowEmpty];

  return (range[0] != null || startMayBeEmpty) && (range[1] != null || endMayBeEmpty);
};

export const normalizeRange = (value: LooseRange | null | undefined): Range | null =>
  value == null ? null : [value[0] ?? null, value[1] ?? null];

export const isSameRange = (a: LooseRange | null | undefined, b: LooseRange | null | undefined) => {
  const [aStart, aEnd] = a ?? [];
  const [bStart, bEnd] = b ?? [];
  return isSameTime(aStart, bStart) && isSameTime(aEnd, bEnd);
};

export const formatRange = (range: Range, format?: RangeFormat): [string, string] => [
  formatValue(range[0], format),
  formatValue(range[1], format),
];

const isSameTime = (a: DateTime | null | undefined, b: DateTime | null | undefined) => {
  if (a == null || b == null) return a == null && b == null;
  return +a === +b;
};

const formatValue = (value: DateTime | null, format?: RangeFormat): string => {
  if (value == null) return '';

  const single = Array.isArray(format) ? format[0] : format;
  if (single == null) return value.toISO() ?? '';
  if (typeof single === 'function') return single(value);
  if (typeof single === 'object') return value.toFormat(single.format);
  return value.toFormat(single);
};
